import React from 'react';
import { Box, Dialog, DialogContent, Portal } from '@mui/material';

type ContentEditableImageToolsProps = {
  children: React.ReactNode;
  editorRef: React.RefObject<HTMLElement | null>;
  onContentChange?: (html: string) => void;
  previewScale?: number;
};

const TOOL_SELECTOR = '[data-content-image-tools="true"]';

const ContentEditableImageTools: React.FC<ContentEditableImageToolsProps> = ({
  children,
  editorRef,
  onContentChange,
  previewScale = 1.5,
}) => {
  const [previewImageSrc, setPreviewImageSrc] = React.useState<string | null>(null);
  const [hoveredImageOverlay, setHoveredImageOverlay] = React.useState<{ top: number; left: number } | null>(null);
  const [previewImageSize, setPreviewImageSize] = React.useState<{ width: number; height: number } | null>(null);
  const selectedImageElementRef = React.useRef<HTMLImageElement | null>(null);
  const previewScrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  const previewDragStateRef = React.useRef({
    active: false,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });

  const clearSelectedImage = React.useCallback(() => {
    setHoveredImageOverlay(null);
    selectedImageElementRef.current = null;
  }, []);

  const updateHoveredImageOverlay = React.useCallback((imageEl: HTMLImageElement | null) => {
    const editor = editorRef.current;
    if (!editor || !imageEl || !editor.contains(imageEl)) {
      setHoveredImageOverlay(null);
      return;
    }

    const imageRect = imageEl.getBoundingClientRect();
    const overlayWidth = 28;
    const overlayHeight = 30;
    const rawTop = imageRect.top + 6;
    const rawLeft = imageRect.right - overlayWidth - 6;

    setHoveredImageOverlay({
      top: Math.max(6, Math.min(rawTop, window.innerHeight - overlayHeight - 6)),
      left: Math.max(6, Math.min(rawLeft, window.innerWidth - overlayWidth - 6)),
    });
  }, [editorRef]);

  const handleMouseMove = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest(TOOL_SELECTOR)) {
      return;
    }

    const imageEl = target?.closest('img') as HTMLImageElement | null;
    if (!imageEl) {
      clearSelectedImage();
      return;
    }

    selectedImageElementRef.current = imageEl;
    updateHoveredImageOverlay(imageEl);
  }, [clearSelectedImage, updateHoveredImageOverlay]);

  const handleMouseLeave = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof HTMLElement && relatedTarget.closest(TOOL_SELECTOR)) {
      return;
    }

    clearSelectedImage();
  }, [clearSelectedImage]);

  const handleClick = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    const imageEl = target?.closest('img') as HTMLImageElement | null;
    const src = imageEl?.getAttribute('src');
    if (!src) {
      return;
    }

    event.preventDefault();
    selectedImageElementRef.current = imageEl;
    setPreviewImageSize(null);
    setPreviewImageSrc(src);
  }, []);

  const handleDeleteImage = React.useCallback(() => {
    const editor = editorRef.current;
    if (!editor) {
      clearSelectedImage();
      return;
    }

    const selectedImage = selectedImageElementRef.current;
    if (selectedImage && editor.contains(selectedImage)) {
      selectedImage.remove();
    }

    onContentChange?.(editor.innerHTML);
    setPreviewImageSize(null);
    setPreviewImageSrc(null);
    clearSelectedImage();
  }, [clearSelectedImage, editorRef, onContentChange]);

  const stopPreviewDrag = React.useCallback(() => {
    const container = previewScrollContainerRef.current;
    previewDragStateRef.current.active = false;
    if (container) {
      container.style.cursor = 'grab';
    }
  }, []);

  React.useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const container = previewScrollContainerRef.current;
      const dragState = previewDragStateRef.current;
      if (!container || !dragState.active) {
        return;
      }

      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;

      container.scrollLeft = dragState.scrollLeft - deltaX;
      container.scrollTop = dragState.scrollTop - deltaY;
      event.preventDefault();
    };

    const handleMouseUp = () => {
      stopPreviewDrag();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [stopPreviewDrag]);

  return (
    <Box onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} onClick={handleClick}>
      {children}

      {hoveredImageOverlay ? (
        <Portal>
          <Box
            data-content-image-tools='true'
            onMouseLeave={clearSelectedImage}
            sx={{
              position: 'fixed',
              top: hoveredImageOverlay.top,
              left: hoveredImageOverlay.left,
              display: 'flex',
              zIndex: 2147483647,
              pointerEvents: 'auto',
            }}
          >
            <Box
              component='button'
              type='button'
              onClick={handleDeleteImage}
              sx={{
                width: 28,
                height: 28,
                p: 0,
                border: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#d32f2f',
                backgroundColor: 'transparent',
                filter: 'drop-shadow(0 1px 1px rgba(255,255,255,0.9))',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Box
                component='svg'
                viewBox='0 0 24 24'
                aria-hidden
                sx={{
                  width: 16,
                  height: 16,
                  display: 'block',
                  fill: 'none',
                  stroke: 'currentColor',
                  strokeWidth: 2.75,
                  strokeLinecap: 'round',
                }}
              >
                <path d='M7 7l10 10M17 7L7 17' />
              </Box>
            </Box>
          </Box>
        </Portal>
      ) : null}

      <Dialog
        open={Boolean(previewImageSrc)}
        onClose={() => {
          setPreviewImageSrc(null);
          setPreviewImageSize(null);
          clearSelectedImage();
        }}
        maxWidth='xl'
      >
        <DialogContent
          ref={previewScrollContainerRef}
          onClick={(event) => {
            event.stopPropagation();
          }}
          onMouseDown={(event) => {
            event.stopPropagation();
            if (event.button !== 0) {
              return;
            }

            const container = previewScrollContainerRef.current;
            if (!container) {
              return;
            }

            const canScrollX = container.scrollWidth > container.clientWidth;
            const canScrollY = container.scrollHeight > container.clientHeight;
            if (!canScrollX && !canScrollY) {
              return;
            }

            previewDragStateRef.current = {
              active: true,
              startX: event.clientX,
              startY: event.clientY,
              scrollLeft: container.scrollLeft,
              scrollTop: container.scrollTop,
            };
            container.style.cursor = 'grabbing';
            event.preventDefault();
          }}
          sx={{
            p: 0,
            backgroundColor: '#111',
            maxWidth: '90vw',
            maxHeight: '85vh',
            overflow: 'auto',
            cursor: 'grab',
            userSelect: 'none',
          }}
        >
          {previewImageSrc ? (
            <Box
              sx={{
                width: previewImageSize ? previewImageSize.width * previewScale : 'fit-content',
                height: previewImageSize ? previewImageSize.height * previewScale : 'fit-content',
                p: 2,
              }}
            >
              <Box
                component='img'
                src={previewImageSrc}
                alt='Preview'
                draggable={false}
                onLoad={(event) => {
                  const imageEl = event.currentTarget;
                  setPreviewImageSize({
                    width: imageEl.naturalWidth || imageEl.width,
                    height: imageEl.naturalHeight || imageEl.height,
                  });
                }}
                sx={{
                  display: 'block',
                  maxWidth: 'none',
                  maxHeight: 'none',
                  width: 'auto',
                  height: 'auto',
                  transform: `scale(${previewScale})`,
                  transformOrigin: 'top left',
                }}
              />
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ContentEditableImageTools;
